/**
 * Super Admin Service
 * Handles all super admin platform management API calls
 */

import apiClient from './api';
import type { ApiResponse } from '../types';
import type {
  DashboardAnalytics,
  UsageTrends,
  TenantDetails,
  ListTenantsParams,
  ListTenantsResponse,
  CreateTenantRequest,
  CreateTenantResponse,
  UpdateTenantRequest,
  UpdateTenantStatusRequest,
  UpdateTenantPlanRequest,
} from '../types/superadmin';

const BASE_URL = '/super-admin';

export const superAdminService = {
  // ==================== Analytics ====================

  /**
   * Get platform-wide dashboard analytics
   */
  getDashboardAnalytics: async (): Promise<DashboardAnalytics> => {
    const response = await apiClient.get<ApiResponse<DashboardAnalytics>>(
      `${BASE_URL}/analytics/dashboard`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch dashboard analytics');
  },

  /**
   * Get usage trends over time
   * @param period - Time period (e.g., '7d', '30d', '90d')
   */
  getUsageTrends: async (period: string = '30d'): Promise<UsageTrends> => {
    const response = await apiClient.get<ApiResponse<UsageTrends>>(
      `${BASE_URL}/analytics/trends`,
      { params: { period } }
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch usage trends');
  },

  // ==================== Tenant List & Details ====================

  /**
   * List all tenants with filters and pagination
   */
  listTenants: async (params?: ListTenantsParams): Promise<ListTenantsResponse> => {
    const response = await apiClient.get<ApiResponse<ListTenantsResponse>>(
      `${BASE_URL}/tenants`,
      { params }
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch tenants');
  },

  /**
   * Get single tenant details
   */
  getTenant: async (tenantId: string): Promise<TenantDetails> => {
    const response = await apiClient.get<ApiResponse<{ tenant: TenantDetails }>>(
      `${BASE_URL}/tenants/${tenantId}`
    );
    if (response.data.success && response.data.data) {
      return response.data.data.tenant;
    }
    throw new Error(response.data.message || 'Failed to fetch tenant details');
  },

  // ==================== Tenant CRUD ====================

  /**
   * Create new tenant with initial admin user
   */
  createTenant: async (data: CreateTenantRequest): Promise<CreateTenantResponse> => {
    const response = await apiClient.post<ApiResponse<CreateTenantResponse>>(
      `${BASE_URL}/tenants`,
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create tenant');
  },

  /**
   * Update tenant details
   */
  updateTenant: async (
    tenantId: string,
    data: UpdateTenantRequest
  ): Promise<TenantDetails> => {
    const response = await apiClient.patch<ApiResponse<{ tenant: TenantDetails }>>(
      `${BASE_URL}/tenants/${tenantId}`,
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data.tenant;
    }
    throw new Error(response.data.message || 'Failed to update tenant');
  },

  /**
   * Delete tenant (soft delete)
   */
  deleteTenant: async (tenantId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse>(
      `${BASE_URL}/tenants/${tenantId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete tenant');
    }
  },

  // ==================== Tenant Status & Plan ====================

  /**
   * Update tenant status
   */
  updateTenantStatus: async (
    tenantId: string,
    data: UpdateTenantStatusRequest
  ): Promise<TenantDetails> => {
    const response = await apiClient.patch<ApiResponse<{ tenant: TenantDetails }>>(
      `${BASE_URL}/tenants/${tenantId}/status`,
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data.tenant;
    }
    throw new Error(response.data.message || 'Failed to update tenant status');
  },

  /**
   * Update tenant subscription plan
   */
  updateTenantPlan: async (
    tenantId: string,
    data: UpdateTenantPlanRequest
  ): Promise<TenantDetails> => {
    const response = await apiClient.patch<ApiResponse<{ tenant: TenantDetails }>>(
      `${BASE_URL}/tenants/${tenantId}/plan`,
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data.tenant;
    }
    throw new Error(response.data.message || 'Failed to update tenant plan');
  },
};
